/**
 * Диалог «Загрузить из файла» мастеров заказа (order-import-legacy-forms): часть клиентов
 * по-прежнему заполняет анкеты старого образца в Excel — «Анкету и форму СОУТ», «Анкету и форму
 * (РИСКИ)», «Список обучающихся». Поток: выбор файла → сервер сразу строит предпросмотр (что
 * попадёт в заказ, какие строки пропущены и почему, что изменится в анкете) → «Загрузить в заказ»
 * отправляет ТОТ ЖЕ файл, и сервер записывает ровно показанный план одной транзакцией.
 *
 * Диалог общий для трёх услуг: что и куда отправлять, задают колбэки `preview`/`importFile`.
 * Для обучения (`departments` задан) при новых сотрудниках без подразделения в файле появляется
 * выбор подразделения, в которое они будут добавлены; не выбрано — «Не указано» (ТЗ п.1.3).
 * `templateUrl` — кнопка «Скачать шаблон» (training-import-template).
 */
import { useState, type ReactNode } from 'react'
import { Download, FileSpreadsheet } from 'lucide-react'
import type {
  Department,
  OrderImportPreview,
  OrderImportResult,
  OrderImportRowStatus,
} from '@/lib/api'
import { fieldError, getErrorMessage } from '@/lib/errors'
import { downloadFromUrl } from '@/lib/download'
import { Dialog, DialogContent } from '@/design-system/components/primitives/dialog'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Badge } from '@/design-system/components/primitives/badge'
import { Alert } from '@/design-system/components/feedback/alert'
import { Field } from '@/design-system/components/primitives/field'
import { FileInput } from '@/design-system/components/primitives/file-input'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'
import {
  DEPARTMENT_SELECT_CHOOSE_FROM_LIST,
  DEPARTMENT_SELECT_EMPLOYEE_PLACEHOLDER,
  DepartmentSelect,
} from '@/company/department-select'

/** Окно выбора файла: только Excel. */
export const ORDER_IMPORT_ACCEPT =
  '.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel'

/** Отказ для файла не Excel — дословно как на сервере. */
export const ORDER_IMPORT_FORMAT_ERROR = 'Выберите файл Excel (.xlsx или .xls).'

/** Подпись кнопки, открывающей диалог, — одна во всех мастерах. */
export const ORDER_IMPORT_BUTTON = 'Загрузить из файла'

/** Подпись кнопки скачивания шаблона файла. */
export const ORDER_IMPORT_TEMPLATE_BUTTON = 'Скачать шаблон'

/** Бейдж результата строки предпросмотра. */
const ROW_STATUS: Record<
  OrderImportRowStatus,
  { label: string; variant: 'green' | 'amber' | 'red' }
> = {
  ok: { label: 'добавится', variant: 'green' },
  warning: { label: 'добавится, проверьте', variant: 'amber' },
  error: { label: 'пропущена', variant: 'red' },
}

/** Обучение: часть добавляемых строк — сотрудники без программы (training-import-template). */
function withoutProgramText(n: number | undefined): string {
  return n ? ` (из них без программы: ${n})` : ''
}

/** true — файл можно отправить на разбор (по расширению; содержимое проверит сервер). */
export function isOrderImportFile(fileName: string): boolean {
  const name = fileName.toLowerCase()
  return name.endsWith('.xlsx') || name.endsWith('.xls')
}

export interface OrderImportDialogProps {
  /** Заголовок диалога, например «Загрузка анкеты СОУТ из файла». */
  title: string
  /** Что за файл ждём и что из него попадёт в заказ. */
  description: ReactNode
  /** Предпросмотр: сервер разбирает файл и строит план загрузки. */
  preview: (file: File) => Promise<OrderImportPreview>
  /** Загрузка того же файла; `departmentName` — подразделение новых сотрудников (обучение). */
  importFile: (file: File, departmentName: string) => Promise<OrderImportResult>
  /**
   * Подразделения структуры для выбора подразделения новых сотрудников (обучение). Не задан —
   * в этой услуге сотрудники не заводятся, поле не показывается.
   */
  departments?: Department[]
  /** Шаблон файла: кнопка «Скачать шаблон» под описанием. */
  templateUrl?: string
  /** Успешная загрузка: вызывающий перечитывает заказ и показывает итог. */
  onImported: (result: OrderImportResult) => void
  onClose: () => void
}

export function OrderImportDialog({
  title,
  description,
  preview,
  importFile,
  departments,
  templateUrl,
  onImported,
  onClose,
}: OrderImportDialogProps) {
  const [file, setFile] = useState<File | null>(null)
  const [plan, setPlan] = useState<OrderImportPreview | null>(null)
  const [checking, setChecking] = useState(false)
  const [importing, setImporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [departmentName, setDepartmentName] = useState('')
  const [departmentError, setDepartmentError] = useState<string | null>(null)

  const busy = checking || importing
  const needsDepartment =
    departments !== undefined && (plan?.newEmployeesWithoutDepartment ?? 0) > 0

  async function selectFile(next: File | null) {
    setFile(next)
    setPlan(null)
    setError(null)
    if (!next) return
    if (!isOrderImportFile(next.name)) {
      setError(ORDER_IMPORT_FORMAT_ERROR)
      return
    }
    setChecking(true)
    try {
      setPlan(await preview(next))
    } catch (err) {
      setError(fieldError(err, 'file') ?? getErrorMessage(err, 'Не удалось прочитать файл'))
    } finally {
      setChecking(false)
    }
  }

  async function confirm() {
    if (!file || !plan) return
    setImporting(true)
    setError(null)
    setDepartmentError(null)
    try {
      const result = await importFile(file, departmentName.trim())
      onImported(result)
      onClose()
    } catch (err) {
      const depErr = fieldError(err, 'departmentName')
      if (depErr) setDepartmentError(depErr)
      else setError(fieldError(err, 'file') ?? getErrorMessage(err, 'Не удалось загрузить файл'))
    } finally {
      setImporting(false)
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !importing) onClose()
      }}
    >
      <DialogContent title={title} className="max-w-3xl">
        <Card
          header={title}
          footer={
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-txt-secondary">
                {plan
                  ? `Добавится: ${plan.toAdd}${withoutProgramText(plan.withoutProgram)} · Пропущено: ${plan.skipped}`
                  : checking
                    ? 'Проверяем файл…'
                    : null}
              </span>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={onClose} disabled={importing}>
                  Отмена
                </Button>
                <Button onClick={() => void confirm()} disabled={busy || !plan || !plan.hasChanges}>
                  {importing ? 'Загрузка…' : 'Загрузить в заказ'}
                </Button>
              </div>
            </div>
          }
        >
          <div className="mb-3 text-txt-secondary">{description}</div>
          {templateUrl ? (
            <div className="mb-3">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => downloadFromUrl(templateUrl)}
                leftIcon={<Download className="size-4 stroke-current" aria-hidden />}
              >
                {ORDER_IMPORT_TEMPLATE_BUTTON}
              </Button>
            </div>
          ) : null}
          {/* Обучение (задан departments) — список обучающихся, СОУТ и профриски — анкета. */}
          <Field label={departments !== undefined ? 'Файл списка обучающихся' : 'Файл анкеты'}>
            {(props) => (
              <FileInput
                {...props}
                value={file}
                onChange={(next) => void selectFile(next)}
                accept={ORDER_IMPORT_ACCEPT}
                disabled={busy}
              />
            )}
          </Field>

          {error ? (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          ) : null}

          {plan ? (
            <div className="flex flex-col gap-3">
              {plan.warnings.length > 0 ? (
                <Alert variant="danger" title="Проверьте перед загрузкой">
                  <ul className="list-disc pl-5">
                    {plan.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </Alert>
              ) : null}
              {plan.notes.length > 0 ? (
                <Alert
                  variant="info"
                  icon={<FileSpreadsheet className="size-5 stroke-current" aria-hidden />}
                  title="Анкета заказа"
                >
                  <ul className="list-disc pl-5">
                    {plan.notes.map((n, i) => (
                      <li key={i}>{n}</li>
                    ))}
                  </ul>
                </Alert>
              ) : null}

              {needsDepartment ? (
                <Field
                  label={`Подразделение для новых сотрудников (${plan.newEmployeesWithoutDepartment})`}
                  hint="Сотрудников, которых нет в структуре компании и у которых в файле не указано подразделение, кабинет добавит в это подразделение с должностью из файла. Не выбрано — в подразделение «Не указано»."
                  error={departmentError ?? undefined}
                >
                  {(props) => (
                    <DepartmentSelect
                      {...props}
                      value={departmentName}
                      departments={departments}
                      deferCreate
                      entryPlaceholder={DEPARTMENT_SELECT_EMPLOYEE_PLACEHOLDER}
                      backToListLabel={DEPARTMENT_SELECT_CHOOSE_FROM_LIST}
                      invalid={Boolean(departmentError)}
                      disabled={busy}
                      onSelect={(name) => {
                        setDepartmentName(name)
                        setDepartmentError(null)
                      }}
                    />
                  )}
                </Field>
              ) : null}

              {plan.rows.length === 0 ? (
                <p className="text-txt-secondary">В файле нет заполненных строк таблицы.</p>
              ) : (
                <div className="max-h-96 overflow-y-auto">
                  <DataTable>
                    <DataTableHead>
                      <DataTableRow>
                        <DataTableHeaderCell>Строка</DataTableHeaderCell>
                        <DataTableHeaderCell>Что добавится</DataTableHeaderCell>
                        <DataTableHeaderCell>Результат</DataTableHeaderCell>
                      </DataTableRow>
                    </DataTableHead>
                    <DataTableBody>
                      {plan.rows.map((row) => (
                        <DataTableRow key={row.rowNumber}>
                          <DataTableCell className="align-top">{row.rowNumber}</DataTableCell>
                          <DataTableCell className="align-top">
                            <p className="font-medium text-txt">{row.title}</p>
                            {row.details ? (
                              <p className="text-12 text-txt-secondary">{row.details}</p>
                            ) : null}
                            {row.messages.length > 0 ? (
                              <ul className="mt-1 list-disc pl-4 text-12 text-txt-secondary">
                                {row.messages.map((m, i) => (
                                  <li key={i}>{m}</li>
                                ))}
                              </ul>
                            ) : null}
                          </DataTableCell>
                          <DataTableCell className="align-top">
                            <Badge variant={ROW_STATUS[row.status].variant}>
                              {ROW_STATUS[row.status].label}
                            </Badge>
                          </DataTableCell>
                        </DataTableRow>
                      ))}
                    </DataTableBody>
                  </DataTable>
                </div>
              )}
            </div>
          ) : null}
        </Card>
      </DialogContent>
    </Dialog>
  )
}
